// Companion hands-on exercises for a course, hosted on GitHub. Keyed by course
// slug so a course can opt in without a backend change. Each exercise's
// `moduleSlug` matches the course module slug AND the directory name in the
// exercises repo, so the GitHub URL is derived, not stored per row.

export interface CourseExercise {
  moduleSlug: string;
  title: string;
  summary: string;
  // Path inside the repo, when it is not the bare `moduleSlug`. The clean-code
  // repo puts each exercise at its module slug; the llms-and-agents labs repo
  // nests them as modules/<module>/<lesson>, because it mirrors the course down
  // to lesson level and one module can hold several directories.
  path?: string;
}

export interface CourseExerciseSet {
  repoUrl: string;
  intro: string;
  // Copy for the link on the course page. Defaults to a per-module claim, which
  // is only true when every module has one. Set it when that is not the case.
  linkLabel?: string;
  // Noun for the count in the hero. Defaults to "exercise".
  noun?: string;
  // "How the exercises work": the shape of the work, and the commands to run it.
  // Defaults describe the clean-code refactoring format, which is not universal.
  howItWorks?: string;
  commands?: string;
  exercises: CourseExercise[];
}

export const COURSE_EXERCISES: Record<string, CourseExerciseSet> = {
  "llms-and-agents": {
    repoUrl: "https://github.com/ehsangazar/self-paced-llms-and-agents",
    // Only the router lab is scaffolded today, so this set deliberately lists
    // one entry. The other five labs are specified end to end in their lessons
    // and built in the learner's own codebase. Add rows here as they land, and
    // keep this honest: the course copy promises exactly what this shows.
    linkLabel: "The workflow router lab has a starter, solution and test suite on GitHub",
    noun: "scaffolded lab",
    howItWorks:
      "A lab ships a starter you fill in, a worked solution published alongside it, and a test file that fails on a fresh clone. The test file is the brief: read it first, then make it pass. Every model call goes through common/llm.ts, so swapping provider or model is a one-line change and the lab still teaches the same thing.",
    commands: "git clone {repo}.git\nnpm install\ncp .env.example .env    # add your OPENROUTER_API_KEY\nnpm test                # the tests are the brief; they fail on a fresh clone\nnpm run lab modules/01-workflows-and-agents/10-lab-workflow-router/starter/index.ts",
    intro:
      "The labs are TypeScript, and every model call goes through one module so the provider stays swappable. The workflow router lab ships a starter, a worked solution, and a test suite that fails on a fresh clone: the tests are the brief. The remaining labs are specified end to end inside their lessons, with the steps, the acceptance criteria and the code shape, and you build them in your own codebase.",
    exercises: [
      {
        moduleSlug: "01-workflows-and-agents",
        path: "modules/01-workflows-and-agents/10-lab-workflow-router",
        title: "Build a workflow router",
        summary:
          "One entry point, three exits: a rules handler that makes no model call at all, a small model, and a large one, with validation and a fallback around every path. The cheapest, highest-leverage pattern in the course, and no agent required.",
      },
    ],
  },
  "clean-code-mastery": {
    repoUrl: "https://github.com/ehsangazar/self-paced-clean-code-mastery",
    intro:
      "Reading about clean code and doing it are different skills. Every module has a hands-on exercise: a working but messy TypeScript file with a passing test suite that pins its behaviour. You refactor it until it reads clean without turning a test red. Clone the repo, run the tests, and check your work against the reference solution.",
    exercises: [
      {
        moduleSlug: "01-foundations",
        title: "Make an unreadable function readable",
        summary:
          "A working checkout function with single-letter names, magic numbers, and three levels of nesting. Refactor it for a reader without changing what it returns.",
      },
      {
        moduleSlug: "02-naming",
        title: "Rename so intent needs no explanation",
        summary:
          "A function whose every name is cryptic or actively misleading, including a variable called list that holds a count. The logic is easy once the names are honest.",
      },
      {
        moduleSlug: "03-functions",
        title: "Split a function that does seven things",
        summary:
          "One function that validates, computes, formats, and logs at once, and mixes a command with a query. Break it into single-purpose pieces.",
      },
      {
        moduleSlug: "04-comments",
        title: "Delete the comments, keep the meaning",
        summary:
          "A function drowning in redundant, outdated, and commented-out noise, with one genuine why-comment buried in it. Make the code self-documenting and keep only the comment that earns its place.",
      },
      {
        moduleSlug: "05-formatting",
        title: "Group and order code for the reader",
        summary:
          "A module whose structure fights the reader: helpers scattered, the entry point buried. Reorder and group it, changing nothing about what it does.",
      },
      {
        moduleSlug: "06-control-flow",
        title: "Flatten nesting with guard clauses",
        summary:
          "An access check nested five levels deep, with each denial reason buried in a distant else. Flatten it into guard clauses while preserving every decision.",
      },
      {
        moduleSlug: "07-variables",
        title: "Shrink scope and kill mutable state",
        summary:
          "A function leaning on wide mutable variables reused for two purposes, plus bare magic numbers. Tighten the scope, prefer values that do not change, and name the constants.",
      },
      {
        moduleSlug: "08-solid",
        title: "Refactor toward dependency inversion",
        summary:
          "A dispatcher that news up its own dependencies and switches on a type string. Introduce an interface and inject the channels so a new one needs no edits to the dispatcher.",
      },
      {
        moduleSlug: "09-error-handling",
        title: "Fail fast at the right boundary",
        summary:
          "A money transfer that validates late and throws one vague error for everything. Move validation to the boundary with guard clauses and typed, specific errors.",
      },
      {
        moduleSlug: "10-testing",
        title: "Write tests that read as a specification",
        summary:
          "Clean production code paired with a deliberately bad test suite. This one inverts the format: the deliverable is the tests, rewritten to read as a spec.",
      },
      {
        moduleSlug: "11-refactoring",
        title: "Cover untested code, then change it safely",
        summary:
          "Gnarly untested legacy code with surprising edge cases. Write characterization tests to build a safety net, then refactor behind it.",
      },
      {
        moduleSlug: "12-professional",
        title: "Review a change and argue the trade-offs",
        summary:
          "A real pull request that works but oversteps its ticket. Write the review: what blocks, what is a nit, and how to push back on the scope creep.",
      },
    ],
  },
};

export function exerciseUrl(repoUrl: string, moduleSlugOrExercise: string | CourseExercise): string {
  const path =
    typeof moduleSlugOrExercise === "string"
      ? moduleSlugOrExercise
      : moduleSlugOrExercise.path ?? moduleSlugOrExercise.moduleSlug;
  return `${repoUrl}/tree/main/${path}`;
}

export function exerciseFor(courseSlug: string | undefined, moduleSlug: string): CourseExercise | undefined {
  if (!courseSlug) return undefined;
  return COURSE_EXERCISES[courseSlug]?.exercises.find((e) => e.moduleSlug === moduleSlug);
}
